import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Container,} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const MySwal = withReactContent(Swal);

const Practice = () => {
  document.title = 'Practice';
  const [isLoading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem('Thyra_Token');
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const permissions = localStorage.getItem("permissionStored");
  const parsedPermissions = JSON.parse(permissions);
  console.log("add_practice permission:", parsedPermissions.add_practice);
  const addPermission = parsedPermissions.add_practice;

  const dataGridStyle = {
    background: 'white',
    fontSize: '10px',
    color: 'black !important',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: 'white',
      color: '#ffffff',
      '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 'bold',
        fontSize: '16px',
      },
    },
    '& .MuiDataGrid-cell': {
      fontSize: '14px',
    },
    '& .MuiDataGrid-cell:hover': {
      color: 'rgba(5, 152, 236, 0.637) !important',
      fontSize: '14.6px !important',
    },
    '& .super-app-theme--header': {
      backgroundColor: '#fff',
      color: '#000',
      padding: '5px',
    },
  };

  // Assuming authR is defined somewhere; if not, remove the conditional or define it
  const authR = { add_practices: true, delete_practices: true }; // Placeholder; adjust as needed

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      cellClassName: 'gridCell',
      sortable: true,
      headerClassName: 'super-app-theme--header',
      flex: 0.03,
    },
    {
      field: 'PracticeName',
      headerName: 'Name',
      cellClassName: 'gridCell',
      sortable: true,
      headerClassName: 'super-app-theme--header',
      flex: 0.3,
      renderCell: (cellValues) => (
        <Button
        sx={{
          color: "#000000",
          textTransform: "none",
          justifyContent: "flex-start",
          fontWeight: "bold",
          fontSize: "0.95rem",
          "&:hover": {
            color: "#195C5F",
            textDecoration: "underline",
            transition: "color 0.2s ease-in-out",
          },
        }}
          onClick={() => openPracticeRegistration(cellValues.row.id)}
        >
          {cellValues.row.PracticeName}
        </Button>
      ),
    },
    {
      field: 'EmailAddress',
      headerName: 'Email Address',
      cellClassName: 'gridCell',
      sortable: true,
      headerClassName: 'super-app-theme--header',
      flex: 0.3,
    },
    {
      field: 'Address',
      headerName: 'Address',
      cellClassName: 'gridCell',
      sortable: true,
      headerClassName: 'super-app-theme--header',
      flex: 0.3,
    },
    {
      field: 'ZipCode',
      headerName: 'Zip Code',
      cellClassName: 'gridCell',
      sortable: true,
      headerClassName: 'super-app-theme--header',
      flex: 0.3,
    },
    {
      field: 'State',
      headerName: 'State',
      cellClassName: 'gridCell',
      sortable: true,
      headerClassName: 'super-app-theme--header',
      flex: 0.3,
    },
    ...(authR.delete_practices
      ? [
          {
            field: 'inactive',
            headerName: 'Action',
            sortable: true,
            headerClassName: 'super-app-theme--header',
            width: 90,
            renderCell: (cellValues) => (
              <DeleteIcon
                style={{
                  marginRight: '5px',
                  marginTop: '5px',
                  color: 'red',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  MySwal.fire({
                    title: 'Are you sure?',
                    text: "You won't be able to revert this!",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, delete it!',
                    cancelButtonText: 'No, cancel!',
                    reverseButtons: true,
                  }).then((result) => {
                    if (result.isConfirmed) {
                      MySwal.fire({
                        title: 'Deleted!',
                        text: 'Your file has been deleted.',
                        icon: 'success',
                        timer: 1000,
                      });

                      axios
                        .post(
                          `${process.env.REACT_APP_API_URL}/Practices/AddPractice`,
                          { id: cellValues.row.id, inactive: true },
                          { headers }
                        )
                        .then(() => setRefresh(!refresh))
                        .catch((error) => console.error('Error:', error));
                    } else if (result.dismiss === Swal.DismissReason.cancel) {
                      MySwal.fire({
                        title: 'Cancelled',
                        text: 'Your file is safe :)',
                        icon: 'error',
                        timer: 1000,
                      });
                    }
                  });
                }}
              />
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/Practices/GetPracticesList`, { headers })
      .then((response) => {
        // Assuming response.data contains the array of practices
        const mappedRows = response.map((item) => ({
          id: item.id,
          PracticeName: item.PracticeName,
          EmailAddress: item.EmailAddress,
          Address: item.Address,
          ZipCode: item.ZipCode,
          State: item.State,
        }));
        setRows(mappedRows);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching practices:', error);
        setRows([]);
        setLoading(false);
      });
  }, [refresh]);

  const openPracticeRegistration = (id = 0) => {
    navigate('/PracticeRegistration', { state: { did: id } });
  };

  return (
    <Container sx={{ pt: 10, pb: 4, maxWidth: 'lg' }}>
       {/* <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2B7B7E" }}>
                Practice 
              </Typography>
            </Box> */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>

        {
          addPermission ? 
          (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
            onClick={() => openPracticeRegistration()}
            sx={{
              bgcolor: '#2B7B7E',
              color: '#FFFFFF',
              borderRadius: '80px',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 1,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                bgcolor: '#77A2A7',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s',
              },
            }}
          >
            Add New Practice
          </Button>
      ): (null)
    }
      </Box>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#7C246C' }} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography sx={{ color: '#77A2A7', fontSize: '1.2rem', fontWeight: 500 }}>
            No practices found
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: 400, width: '100%', marginTop: '1.5rem' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            sx={dataGridStyle}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
          />
        </Box>
      )}
    </Container>
  );
};

export default Practice;